import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../../api/axiosClient";
import { decryptedAdminID, getStageName } from "../../utility";

const admin = Number(decryptedAdminID());

// ✅ BASE FILTER
const baseFilters = (type) => ({
  stage_id: null,
  sub_stage_id: null,
  reason_id: null,
  first_name: null,
  mobile_number: null,
  company_name: null,
  date_from: null,
  date_to: null,
  limit: 10,
  offset: 0,
  admin_ids: admin ? [admin] : [],
  type,
});

// ✅ INITIAL STATE
const initialState = {
  currentType: "ACTIVE",

  pipelineByType: {
    ACTIVE: {
      filters: baseFilters("ACTIVE"),
      columnFilters: {},
      pageMap: {},
       redDotMap: {}, 
      rawData: [],
      kanbanData: [],
    },
    EXIT: {
      filters: baseFilters("EXIT"),
      columnFilters: {},
      pageMap: {},
       redDotMap: {}, 
      rawData: [],
      kanbanData: [],
    },
  },

  loading: false,
  error: null,
};
// ✅ Debounce Helper (GLOBAL)
let debounceTimer;

const debounceApi = (fn, delay = 500) => {
  return (...args) =>
    new Promise((resolve, reject) => {
      if (debounceTimer) clearTimeout(debounceTimer);

      debounceTimer = setTimeout(async () => {
        try {
          const result = await fn(...args);
          resolve(result);
        } catch (err) {
          reject(err);
        }
      }, delay);
    });
};

// ✅ Debounced API function
const debouncedFetchPipelineApi = debounceApi(async (finalFilters) => {
  const res = await axiosClient.post("/getPipeline", finalFilters);
  return res;
}, 500);
// ✅ FETCH API
// export const fetchPipeline = createAsyncThunk(
//   "pipeline/fetchPipeline",
//   async (payload = {}, { getState, rejectWithValue }) => {
//     try {
//       const { currentType, pipelineByType } = getState().pipeline;
//       const currentState = pipelineByType[currentType];

//       const stageId = payload.stage_id;

//       const commonFilters = currentState.filters;

//       const stageFilters =
//         stageId && currentState.columnFilters[stageId]
//           ? currentState.columnFilters[stageId]
//           : {};

//       const finalFilters = {
//         ...commonFilters,
//         ...payload,
//         ...stageFilters,
//       };

//       const res = await axiosClient.post("/getPipeline", finalFilters);

//       return {
//         data: res.data.data || [],
//         type: currentType,
//         stage_id: payload.stage_id || null,
//       };
//     } catch (err) {
//       return rejectWithValue(err.response?.data || "Something went wrong");
//     }
//   }
// );

// export const fetchPipeline = createAsyncThunk(
//   "pipeline/fetchPipeline",
//   async (payload = {}, { getState, rejectWithValue }) => {
//     try {
//       const { currentType, pipelineByType } = getState().pipeline;
//       const currentState = pipelineByType[currentType];

//       const stageId = payload.stage_id;

//       const commonFilters = currentState.filters;

//       const stageFilters =
//         stageId && currentState.columnFilters[stageId]
//           ? currentState.columnFilters[stageId]
//           : {};

//       const finalFilters = {
//         ...commonFilters,
//         ...payload,
//         ...stageFilters,
//       };

//       // ✅ Debounced API Call
//       const res = await debouncedFetchPipelineApi(finalFilters);

//       return {
//         data: res.data.data || [],
//         type: currentType,
//         stage_id: payload.stage_id || null,
//       };
//     } catch (err) {
//       return rejectWithValue(err.response?.data || "Something went wrong");
//     }
//   }
// );
export const fetchPipeline = createAsyncThunk(
  "pipeline/fetchPipeline",
  async (payload = {}, { getState, rejectWithValue }) => {
    try {
      const { currentType, pipelineByType } = getState().pipeline;
      const currentState = pipelineByType[currentType];

      const stageId = payload.stage_id;
      const today = new Date().toISOString().split("T")[0];

      const commonFilters = currentState.filters;

      const stageFilters =
        stageId && currentState.columnFilters[stageId]
          ? currentState.columnFilters[stageId]
          : {};

      // ✅ ONLY handle Stage 8 when stage_id = 8
      const defaultStage8Filter =
        stageId === 8 &&
        !payload.date_from &&
        !stageFilters.date_from
          ? {
              date_from: "2025-12-01",
              date_to: today,
            }
          : {};

      const finalFilters = {
        ...commonFilters,
        ...stageFilters,
        ...defaultStage8Filter,
        ...payload,
      };

      console.log("FINAL API FILTER:", finalFilters);

      const res = await debouncedFetchPipelineApi(finalFilters);

      return {
        data: res.data.data || [],
        type: currentType,
        stage_id: payload.stage_id || null,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Something went wrong");
    }
  }
);

// ✅ MAP DATA + TYPE ADD
const mapKanbanData = (data, pipelineType) =>
  data.map((stage) => {
    const stageId = Number(stage.current_stage_id);

    let type = "INTERVIEW";

    // ACTIVE → 1,6 = LEAD
    if (pipelineType === "ACTIVE" && [1, 6].includes(stageId)) {
      type = "LEAD";
    }

    // EXIT → 31 = LEAD
    if (pipelineType === "EXIT" && stageId === 31) {
      type = "LEAD";
    }

    return {
      stage_id: stageId,
      stage: getStageName(stage.current_stage_id),
      total_candidates: stage.total_candidates,
      page_limit: stage.page_limit,
      type, // ⭐ IMPORTANT

      candidates: (stage.candidates || []).map((c) => ({
        id: c.interview_id,
        name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
        role: c.job_category,
        phone: c.mobile_number,
        companyName: c.company_name,
        date: c.event_date,
        admin: c.admin_name,
        admin_id: c.admin_id,
        candidate_id: c.candidate_id,
        interview_id: c.interview_id,
        current_stage_id: c.current_stage_id,
        current_sub_stage_id: c.current_sub_stage_id,
        current_sub_stage: c.current_sub_stage,
        from_stage_id:c.from_stage_id,
        to_stage_id:c.to_stage_id,
        reason: c.reason,
      })),
    };
  });

const pipelineSlice = createSlice({
  name: "pipeline",
  initialState,

  reducers: {
    // setPipelineType: (state, action) => {
    //   state.currentType = action.payload;
    // },
    setPipelineType: (state, action) => {
  state.currentType = action.payload;

  const type = action.payload;

  if (!state.pipelineByType[type].filters.admin_ids.length) {
    state.pipelineByType[type].filters.admin_ids = [];
  }
},

    updateFilter: (state, action) => {
      const type = state.currentType;
      Object.assign(state.pipelineByType[type].filters, action.payload);
    },

    resetFilter: (state) => {
      const type = state.currentType;
      state.pipelineByType[type].filters = baseFilters(type);
    },

    // updateColumnFilter: (state, action) => {
    //   const type = state.currentType;
    //   const { stageId, filters } = action.payload;
    //   state.pipelineByType[type].columnFilters[stageId] = filters;
    // },

    updateColumnFilter: (state, action) => {
  const type = state.currentType;
  const { stageId, filters } = action.payload;

  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(
      ([_, value]) =>
        value !== null &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0)
    )
  );

  if (Object.keys(cleanFilters).length === 0) {
    // ✅ REMOVE completely
    delete state.pipelineByType[type].columnFilters[stageId];
    delete state.pipelineByType[type].redDotMap[stageId];
  } else {
    state.pipelineByType[type].columnFilters[stageId] = cleanFilters;
    state.pipelineByType[type].redDotMap[stageId] = true;
  }
},

    // clearColumnFilter: (state, action) => {
    //   const type = state.currentType;
    //   delete state.pipelineByType[type].columnFilters[action.payload];
    // },

//     clearColumnFilter: (state, action) => {
//   const type = state.currentType;
//   const stageId = action.payload;

//   delete state.pipelineByType[type].columnFilters[stageId];
//   state.pipelineByType[type].redDotMap[stageId] = false; // ❌ hide
// },
clearColumnFilter: (state, action) => {
  const type = state.currentType;
  const stageId = action.payload;

  delete state.pipelineByType[type].columnFilters[stageId];
  delete state.pipelineByType[type].redDotMap[stageId]; // ✅ important
},
clearAllColumnFilters: (state) => {
  const type = state.currentType;

  state.pipelineByType[type].columnFilters = {};
  state.pipelineByType[type].redDotMap = {}; // ❌ clear all dots
},

    setPage: (state, action) => {
      const type = state.currentType;
      const { stageId, page } = action.payload;
      state.pipelineByType[type].pageMap[stageId] = page;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchPipeline.pending, (state) => {
        state.loading = true;
      })

      .addCase(fetchPipeline.fulfilled, (state, action) => {
        state.loading = false;

        const { type, data, stage_id } = action.payload;

        const mappedData = mapKanbanData(data, type); // ⭐ FIX
        const currentState = state.pipelineByType[type];

        // ✅ INITIAL LOAD
        if (!stage_id) {
          currentState.kanbanData = mappedData;
          currentState.rawData = data;
          return;
        }

        // ✅ UPDATE SINGLE STAGE
        const updatedStage = mappedData.find(
          (s) => s.stage_id === stage_id
        );

        if (!updatedStage) return;

        currentState.kanbanData = currentState.kanbanData.map(
          (stage) =>
            stage.stage_id === stage_id ? updatedStage : stage
        );

        currentState.rawData = data;
      })

      .addCase(fetchPipeline.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setPipelineType,
  updateFilter,
  resetFilter,
  updateColumnFilter,
  clearColumnFilter,
  clearAllColumnFilters,
  setPage,
} = pipelineSlice.actions;

export default pipelineSlice.reducer;